import React, {useId} from 'react';

import PrivacyHeaderIcon from '../img/privacy-header.svg';

const sectionHeadingClassName = 'mb-4 text-2xl font-bold';
const paragraphClassName = 'mb-4';
const dividerClassName = 'my-8 border-t border-(--compass-border)';
// The 44px minimum height keeps every standalone link a full pointer target.
const linkClassName =
  'inline-flex min-h-11 items-center rounded-sm text-(--compass-primary) underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)';
const cellClassName = 'border-b border-(--compass-border) px-2 py-3 align-top sm:px-3';

function createData(name, description, isShared) {
  return {name, description, isShared};
}

const typesRows = [
  createData(
    'Account information',
    'When you use Compass, you create a profile which includes your username and email address. This allows you to access your profile and to use Compass. We will send you password reminders or other essential account information by email. We will send you reminders by email or SMS if you ask us to.',
    'Never'
  ),
  createData(
    'Contact information',
    'When you provide us with contact information we will use it to send you information about the training courses that we run. You will be able to choose how we contact you: by email or SMS. We will always provide an easy way to opt out of receiving those messages.',
    'Only internally, within ARC'
  ),
  createData(
    'Professional profile',
    'We will ask you to provide your job title, the sector or industry that you work in and the geographical area where you work.',
    'Some of this information will be aggregated and shared in an anonymous way with the local authority area/s where you work.'
  ),
  createData(
    'Transition information',
    'We will ask you questions about your experience of transition for young people.',
    'This will be shared in an anonymous and aggregated form.'
  ),
];

const lawfulBasisRows = [
  createData(
    'Contractual Obligation',
    'We process Account Information to provide you with a user account and we ask questions about you so that we can ensure you get the relevant information.'
  ),
  createData(
    'Legitimate Interest',
    'We are using the responses you provide to our questions about the transition process and your evaluation of that so we can work with other organisations to improve the journey. We believe that this a good reason for us to ask personal questions but we have balanced this against your privacy interests. Therefore, we only share aggregated and anonymised information.'
  ),
];

const rightsRows = [
  createData(
    'Request access to your personal information',
    'Commonly known as a "data subject access request". This enables you to receive a copy of the personal information we hold about you and to check that we are lawfully processing it.'
  ),
  createData(
    'Request correction of the personal information that we hold about you',
    'This enables you to have any incomplete or inaccurate information we hold about you corrected.'
  ),
  createData(
    'Request erasure of your personal information',
    'This enables you to ask us to delete or remove personal information where there is no good reason for us continuing to process it. You also have the right to ask us to delete or remove your personal information where you have exercised your right to object to processing (see below).'
  ),
  createData(
    'Request the restriction of processing of your personal information',
    'You would make this request if, for example, you want to establish its accuracy or the reason for processing it. You would generally ask for this restriction when you are asking for other information.'
  ),
  createData(
    'Your right to data portability',
    'You have the right to ask that we transfer the personal information you gave us to another organisation, or to you, in certain circumstances.'
  ),
  createData(
    'Object to the processing of your personal information',
    'If we are relying on your consent, or a legitimate interest (or those of a third party) and there is something about your particular situation which makes you want to object to processing on this basis.'
  ),
];

/**
 * A data table named by its section heading. There is no minimum width: the
 * cells wrap between words, and a word longer than its column breaks rather
 * than pushing the page sideways at 390px or 400% zoom. Each row is
 * headed by its first cell, so every value keeps its row and column.
 */
const PrivacyTable = ({labelledBy, columns, rows}) => (
  <table
    aria-labelledby={labelledBy}
    className="mb-4 w-full border-collapse text-left text-sm break-words text-(--compass-on-surface)">
    <thead>
      <tr>
        {columns.map((column) => (
          <th key={column} scope="col" className={`${cellClassName} font-bold`}>
            {column}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.name}>
          <th scope="row" className={`${cellClassName} font-bold`}>
            {row.name}
          </th>
          <td className={cellClassName}>{row.description}</td>
          {columns.length > 2 ? <td className={cellClassName}>{row.isShared}</td> : null}
        </tr>
      ))}
    </tbody>
  </table>
);

const ProPrivacyNotice = () => {
  const id = useId();
  const typesHeadingId = `${id}-types`;
  const lawfulBasisHeadingId = `${id}-lawful-basis`;
  const rightsHeadingId = `${id}-rights`;

  return (
    <div
      data-testid="pro-privacy-notice"
      className="bg-(--compass-surface) text-(--compass-on-surface)">
      <header className="bg-(--compass-primary) pb-8 text-(--compass-on-primary)">
        <div className="mx-auto w-full max-w-[900px] px-4 pt-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="w-4/5 text-3xl font-bold break-words sm:w-3/5">Privacy Notice</h1>
            <img aria-hidden="true" src={PrivacyHeaderIcon} height={90} width={90} alt="" />
          </div>
        </div>
      </header>
      <div className="mx-auto w-full max-w-[900px] px-4 py-8 md:py-10">
        <h2 className={sectionHeadingClassName}>Summary</h2>
        <p className={paragraphClassName}>
          Compass was created and is operated by ARC Scotland. ARC Scotland set up the Scottish
          Transition Forum and has been working with the Scottish Government, Local Authorities and
          other organisations to improve the experience of children and young adults (14 to 25
          years) with additional support needs as they make the transition to young adult life.
        </p>
        <p className={paragraphClassName}>
          Compass is part of this work and that project had the following aims:
        </p>
        <ul className="mb-4 list-disc space-y-2 pl-6">
          <li>
            To provide parents or guardians of a young person with additional support needs with
            relevant information at each transition stage reached;
          </li>
          <li>
            To share the insights provided by parents and guardians to help improve the transition
            experience; and
          </li>
          <li>
            To provide relevant information to professionals working in this area and to gather
            their feedback.
          </li>
        </ul>
        <p className={paragraphClassName}>
          To do that we ask you to sign up to use Compass and then to answer various questions about
          yourself and your profession; about your involvement in the transition journey; and to
          evaluate that journey. This will allow us to provide you with information that is relevant
          to you.
        </p>
        <p className={paragraphClassName}>
          We will share a limited amount of profile information, transition information and
          evaluation information with local authorities. Any information shared will be aggregated
          and anonymised which means it will be pooled together in a way that means it cannot be
          linked back to you.
        </p>
        <p className={paragraphClassName}>
          We will also share information with the Scottish Government. We are working in partnership
          with them to help improve the transition experience. They will use the information to
          evaluate the project and this data is aggregated and anonymised.
        </p>
        <p className={paragraphClassName}>
          We will publish reports from time to time which will contain information obtained through
          Compass but which will never identify any individual. These reports will be publicly
          available with the same aim of improving the transition process.
        </p>
        <p className={paragraphClassName}>
          This privacy notice is here to help you understand what we are doing with your personal
          data; how we are keeping it safe and to tell you about the rights that you have in
          relation to your personal data. We want you to feel comfortable that the personal
          information you are giving to us will be looked after.
        </p>
        <hr className={dividerClassName} />
        <h2 className={sectionHeadingClassName}>Our contact details</h2>
        <p className="mb-2 ml-4">
          <strong>Name</strong>: Association for Real Change
        </p>
        <p className="mb-2 ml-4">
          <strong>Address</strong>: ARC House, Marden Street, Chesterfield, Derbyshire S40 1JY
        </p>
        <p className="mb-2 ml-4">
          <strong>Email</strong>:{' '}
          <a
            className={linkClassName}
            href="mailto:compass@arcuk.org.uk"
            target="_blank"
            rel="noreferrer">
            compass@arcuk.org.uk
          </a>
        </p>
        <hr className={dividerClassName} />
        <h2 id={typesHeadingId} className={sectionHeadingClassName}>
          The type of personal information we collect and what we do with it
        </h2>
        <p className={paragraphClassName}>
          We collect and use the following information through Compass:
        </p>
        <PrivacyTable
          labelledBy={typesHeadingId}
          columns={['Type of information', 'Purpose', 'Shared with another controller?']}
          rows={typesRows}
        />
        <hr className={dividerClassName} />
        <h2 className={sectionHeadingClassName}>
          How we get the personal information and why we have it
        </h2>
        <p className={paragraphClassName}>
          The only source of information about you is from you. We do not gather any more
          information about you from other places. It is only the information that you give to us
          that we use.
        </p>
        <p className={paragraphClassName}>
          You may have heard about something called Cookies. These allow websites and Apps to
          function better but can also be used to gather a lot of information about how you behave
          online. We use Cookies which are necessary to allow Compass to function and recognise you.
          We also use analytics cookies, provided by PostHog Cloud EU, to understand how Compass is
          used so we can improve it. Analytics cookies are only set if you give your consent.
        </p>
        <h2 id={lawfulBasisHeadingId} className={sectionHeadingClassName}>
          Lawful Basis
        </h2>
        <p className={paragraphClassName}>
          Under the General Data Protection Regulation (GDPR), we need to have a lawful basis to use
          your information. We have set these out below.
        </p>
        <PrivacyTable
          labelledBy={lawfulBasisHeadingId}
          columns={['Lawful Basis for collecting the data', 'Purpose']}
          rows={lawfulBasisRows}
        />
        <hr className={dividerClassName} />
        <h2 className={sectionHeadingClassName}>If you do not provide us with personal data</h2>
        <p className={paragraphClassName}>
          We hope that the information in this notice reassures you about why we are asking for your
          personal data, but if you do not wish to provide the information we ask for, Compass will
          not work properly and we may be unable to provide you with relevant information.
        </p>
        <hr className={dividerClassName} />
        <h2 className={sectionHeadingClassName}>How we store your personal information</h2>
        <p className={paragraphClassName}>
          We work closely with a technology company called Wheelhouse who helped us to develop
          Compass and who keep it working properly. They also store the information gathered in
          Compass on their database on our behalf.
        </p>
        <p className={paragraphClassName}>
          Wheelhouse are legally called a processor which means that they provide us with the
          services we asked them to, under a legal contract we have with them. The terms of this
          contract mean that they cannot do anything with your information unless we have instructed
          them to do that. They will not share your personal information with any organisation apart
          from us or as instructed by us. They will hold it securely and only for as long as we
          instruct them to.
        </p>
        <p className={paragraphClassName}>
          Wheelhouse work with organisations like ARC to co-design websites and mobile applications
          with the aim of creating technology that is designed to work for everyone, including
          disabled and neurodiverse people.
        </p>
        <p className={paragraphClassName}>
          Wheelhouse uses a database run by Amazon Web Services which is hosted in the UK and other
          services to send your email and SMS reminders if requested. Their processes have been
          secured using cyber security expert advice.
        </p>
        <p className={paragraphClassName}>
          The information gathered by Compass is encrypted when it is transferred between your
          device and our database. It is also encrypted as it is stored. Before information is
          shared with local authorities and the Scottish Government, it is separated from your
          personal details, combined with other information and only this aggregated and anonymised
          information is shared.
        </p>
        <hr className={dividerClassName} />
        <h2 className={sectionHeadingClassName}>How long we hold onto your data</h2>
        <p className={paragraphClassName}>
          We keep your account open until you tell us to close it. Or if it is five years since you
          have used your account, we will contact you and unless you tell us otherwise, we will
          close your account. We will contact you using the email address or phone number you
          provided to us. Once your account is closed, your account information will be securely
          deleted from our database.
        </p>
        <p className={paragraphClassName}>
          We will retain the other information that you provided to us, but this will be anonymous.
          This will continue to assist us to improve the transition journey for young people in
          Scotland.
        </p>
        <hr className={dividerClassName} />
        <h2 id={rightsHeadingId} className={sectionHeadingClassName}>
          Your data protection rights
        </h2>
        <p className={paragraphClassName}>Under data protection law, you have rights including:</p>
        <PrivacyTable
          labelledBy={rightsHeadingId}
          columns={['Your Rights', 'What they mean in practice']}
          rows={rightsRows}
        />
        <p className={paragraphClassName}>
          You are not required to pay anything for exercising your rights. If you make a request, we
          have one month to respond to you.
        </p>
        <hr className={dividerClassName} />
        <h2 className={sectionHeadingClassName}>Queries or concerns</h2>
        <p className={paragraphClassName}>
          Please contact us using the contact details above to make a request or to raise any
          concerns you have about how the personal data gathered through Compass is being used. You
          can also contact the Information Commissioner’s Office if you have concerns and their
          contact details can be found here:{' '}
          <a
            className={linkClassName}
            href="https://ico.org.uk/global/contact-us/"
            target="_blank"
            rel="noreferrer">
            ico.org.uk/global/contact-us/
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export default ProPrivacyNotice;
